import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Admin.css';

axios.defaults.withCredentials = true;

function Admin() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('users');

  const [users, setUsers] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [serialCerts, setSerialCerts] = useState([]);
  const [generalCerts, setGeneralCerts] = useState([]);
  const [softoneCerts, setSoftoneCerts] = useState([]);

  const [uploadFiles, setUploadFiles] = useState(null);
  const [certFiles, setCertFiles] = useState(null);
  const [certExpiry, setCertExpiry] = useState('');
  const [serialFiles, setSerialFiles] = useState(null);
  const [generalFiles, setGeneralFiles] = useState(null);
  const [softoneFiles, setSoftoneFiles] = useState(null);

  const [editingExpiryId, setEditingExpiryId] = useState(null);
  const [newExpiryDate, setNewExpiryDate] = useState('');

  const [message, setMessage] = useState('');

  useEffect(() => {
    axios.get('/api/me')
      .then(res => {
        if (!res.data.logged_in || (res.data.username !== 'admin' && res.data.username !== 'SimeDrawingsAdmin')) {
          navigate('/');
        } else {
          setMe(res.data);
        }
      })
      .catch(() => navigate('/login'));
  }, [navigate]);

  useEffect(() => {
    fetchUsers();
    fetchDrawings();
    fetchCertificates();
    fetchSerialCerts();
    fetchGeneralCerts();
    fetchSoftoneCerts();
  }, []);

  const fetchUsers = () => {
    axios.get('/api/users').then(res => setUsers(res.data)).catch(() => {});
  };

  const fetchDrawings = () => {
    axios.get('/api/drawings').then(res => setDrawings(res.data)).catch(() => {});
  };

  const fetchCertificates = () => {
    axios.get('/api/certificates').then(res => setCertificates(res.data)).catch(() => {});
  };

  const fetchSerialCerts = () => {
    axios.get('/api/serial_certs').then(res => setSerialCerts(res.data)).catch(() => {});
  };

  const fetchGeneralCerts = () => {
    axios.get('/api/general_certs').then(res => setGeneralCerts(res.data)).catch(() => {});
  };

  const fetchSoftoneCerts = () => {
    axios.get('/api/softone_certs').then(res => setSoftoneCerts(res.data)).catch(() => {});
  };

  // ── Users ─────────────────────────────────────────
  const approveUser = (id) => {
    axios.post(`/admin/approve/${id}`).then(() => fetchUsers());
  };

  const rejectUser = (id) => {
    axios.post(`/admin/reject/${id}`).then(() => fetchUsers());
  };

  const deleteUser = (id) => {
    if (!window.confirm('Σίγουρα θέλετε να διαγράψετε τον χρήστη;')) return;
    axios.post(`/admin/delete_user/${id}`).then(() => fetchUsers());
  };

  // ── Drawings ──────────────────────────────────────
  const handleUploadDrawings = (e) => {
    e.preventDefault();
    if (!uploadFiles) return;
    const form = e.target;
    const formData = new FormData();
    for (let i = 0; i < uploadFiles.length; i++) {
      formData.append('file', uploadFiles[i]);
    }
    axios.post('/admin/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(() => {
      setMessage('✅ Τα σχέδια ανέβηκαν επιτυχώς!');
      setUploadFiles(null);
      form.reset();
      fetchDrawings();
    }).catch(() => setMessage('❌ Σφάλμα κατά το ανέβασμα.'));
  };

  const deleteDrawing = (id) => {
    if (!window.confirm('Σίγουρα θέλετε να διαγράψετε το σχέδιο;')) return;
    axios.post(`/admin/delete_drawing/${id}`).then(() => fetchDrawings());
  };

  const clearDuplicateDrawings = () => {
    axios.post('/admin/clear_duplicates_drawings').then(res => {
      setMessage(`🧹 Διαγράφηκαν ${res.data.deleted} διπλότυπα σχέδια.`);
      fetchDrawings();
    });
  };

  const copyDrawingLink = (filename) => {
    const link = `${window.location.origin}/sime/${filename}`;
    navigator.clipboard.writeText(link);
    setMessage('🔗 Ο σύνδεσμος αντιγράφηκε!');
  };

  // ── Certificates ──────────────────────────────────
  const handleUploadCert = (e) => {
    e.preventDefault();
    if (!certFiles) return;
    const form = e.target;
    const formData = new FormData();
    for (let i = 0; i < certFiles.length; i++) {
      formData.append('file', certFiles[i]);
    }
    if (certExpiry) formData.append('expiry_date', certExpiry);
    axios.post('/admin/upload_cert', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(() => {
      setMessage('✅ Τα πιστοποιητικά ανέβηκαν επιτυχώς!');
      setCertFiles(null);
      setCertExpiry('');
      form.reset();
      fetchCertificates();
    }).catch(() => setMessage('❌ Σφάλμα κατά το ανέβασμα.'));
  };

  const deleteCert = (id) => {
    if (!window.confirm('Σίγουρα θέλετε να διαγράψετε το πιστοποιητικό;')) return;
    axios.post(`/admin/delete_cert/${id}`).then(() => fetchCertificates());
  };

  const clearDuplicateCerts = () => {
    axios.post('/admin/clear_duplicates_certs').then(res => {
      setMessage(`🧹 Διαγράφηκαν ${res.data.deleted} διπλότυπα πιστοποιητικά.`);
      fetchCertificates();
    });
  };

  const copyCertLink = (filename) => {
    const link = `${window.location.origin}/cert/${filename}`;
    navigator.clipboard.writeText(link);
    setMessage('🔗 Ο σύνδεσμος αντιγράφηκε!');
  };

  const startEditExpiry = (id, currentDate) => {
    setEditingExpiryId(id);
    setNewExpiryDate(currentDate || '');
  };

  const saveExpiry = (id) => {
    axios.post(`/admin/update_expiry/${id}`, { expiry_date: newExpiryDate })
      .then(() => {
        setEditingExpiryId(null);
        setNewExpiryDate('');
        fetchCertificates();
      });
  };

  const checkExpiredCerts = () => {
    axios.post('/admin/check_expired').then(res => {
      setMessage(`📧 Στάλθηκε email για ${res.data.expired_count} ληγμένα πιστοποιητικά.`);
    }).catch(() => setMessage('❌ Σφάλμα κατά τον έλεγχο ληξιπρόθεσμων.'));
  };

  // ── Serial Certs ──────────────────────────────────
  const handleUploadSerial = (e) => {
    e.preventDefault();
    if (!serialFiles) return;
    const form = e.target;
    const formData = new FormData();
    for (let i = 0; i < serialFiles.length; i++) {
      formData.append('file', serialFiles[i]);
    }
    axios.post('/admin/upload_serial', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(() => {
      setMessage('✅ Τα serial πιστοποιητικά ανέβηκαν επιτυχώς!');
      setSerialFiles(null);
      form.reset();
      fetchSerialCerts();
    }).catch(() => setMessage('❌ Σφάλμα κατά το ανέβασμα.'));
  };

  const deleteSerial = (id) => {
    if (!window.confirm('Σίγουρα θέλετε να διαγράψετε το πιστοποιητικό;')) return;
    axios.post(`/admin/delete_serial/${id}`).then(() => fetchSerialCerts());
  };

  const copySerialLink = (filename) => {
    const link = `${window.location.origin}/serial/${filename}`;
    navigator.clipboard.writeText(link);
    setMessage('🔗 Ο σύνδεσμος αντιγράφηκε!');
  };

  // ── General Certs ─────────────────────────────────
  const handleUploadGeneral = (e) => {
    e.preventDefault();
    if (!generalFiles) return;
    const form = e.target;
    const formData = new FormData();
    for (let i = 0; i < generalFiles.length; i++) {
      formData.append('file', generalFiles[i]);
    }
    axios.post('/admin/upload_general', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(() => {
      setMessage('✅ Τα γενικά πιστοποιητικά ανέβηκαν επιτυχώς!');
      setGeneralFiles(null);
      form.reset();
      fetchGeneralCerts();
    }).catch(() => setMessage('❌ Σφάλμα κατά το ανέβασμα.'));
  };

  const deleteGeneralCert = (id) => {
    if (!window.confirm('Σίγουρα θέλετε να διαγράψετε το πιστοποιητικό;')) return;
    axios.post(`/admin/delete_general/${id}`).then(() => fetchGeneralCerts());
  };

  const copyGeneralLink = (filename) => {
    const link = `${window.location.origin}/general/${filename}`;
    navigator.clipboard.writeText(link);
    setMessage('🔗 Ο σύνδεσμος αντιγράφηκε!');
  };

  // ── SoftOne Certs ─────────────────────────────────
  const handleUploadSoftone = (e) => {
    e.preventDefault();
    if (!softoneFiles) return;
    const form = e.target;
    const formData = new FormData();
    for (let i = 0; i < softoneFiles.length; i++) {
      formData.append('file', softoneFiles[i]);
    }
    axios.post('/admin/upload_softone', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(() => {
      setMessage('✅ Τα πιστοποιητικά SoftOne ανέβηκαν επιτυχώς!');
      setSoftoneFiles(null);
      form.reset();
      fetchSoftoneCerts();
    }).catch(() => setMessage('❌ Σφάλμα κατά το ανέβασμα.'));
  };

  const deleteSoftoneCert = (id) => {
    if (!window.confirm('Σίγουρα θέλετε να διαγράψετε το πιστοποιητικό;')) return;
    axios.post(`/admin/delete_softone/${id}`).then(() => fetchSoftoneCerts());
  };

  const copySoftoneLink = (filename) => {
    const link = `${window.location.origin}/softone/${filename}`;
    navigator.clipboard.writeText(link);
    setMessage('🔗 Ο σύνδεσμος αντιγράφηκε!');
  };

  if (!me) return <div className="admin-loading">Φόρτωση...</div>;

  return (
    <div className="admin-wrapper">
      <h2>⚙️ Admin Panel</h2>

      {message && <div className="admin-message">{message}</div>}

      <div className="admin-ribbon">
        <div className={`ribbon-tab ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>
          👥 Χρήστες
        </div>
        <div className={`ribbon-tab ${tab === 'drawings' ? 'active' : ''}`} onClick={() => setTab('drawings')}>
          📐 Σχέδια
        </div>
        <div className={`ribbon-tab ${tab === 'certificates' ? 'active' : ''}`} onClick={() => setTab('certificates')}>
          📜 Πιστοποιητικά
        </div>
        <div className={`ribbon-tab ${tab === 'serial' ? 'active' : ''}`} onClick={() => setTab('serial')}>
          🔢 Serial Certs
        </div>
        <div className={`ribbon-tab ${tab === 'general' ? 'active' : ''}`} onClick={() => setTab('general')}>
          📄 Γενικά Πιστοποιητικά
        </div>
        <div className={`ribbon-tab ${tab === 'softone' ? 'active' : ''}`} onClick={() => setTab('softone')}>
          🧾 Πιστοποιητικά SoftOne
        </div>
      </div>

      {tab === 'users' && (
        <div className="admin-section">
          <h3>Χρήστες</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Κατάσταση</th>
                <th>Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>{u.username}</td>
                  <td>{u.email}</td>
                  <td>{u.approved ? '✅ Εγκεκριμένος' : '⏳ Εκκρεμεί'}</td>
                  <td>
                    {!u.approved && (
                      <button onClick={() => approveUser(u.id)}>Έγκριση</button>
                    )}
                    <button onClick={() => rejectUser(u.id)}>Απόρριψη</button>
                    <button className="danger" onClick={() => deleteUser(u.id)}>Διαγραφή</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'drawings' && (
        <div className="admin-section">
          <h3>Σχέδια</h3>
          <form onSubmit={handleUploadDrawings} className="upload-form">
            <input type="file" multiple onChange={e => setUploadFiles(e.target.files)} />
            <button type="submit">⬆️ Ανέβασμα</button>
          </form>
          <button onClick={clearDuplicateDrawings}>🧹 Καθαρισμός διπλότυπων</button>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Όνομα</th>
                <th>Ημ/νία Ανεβάσματος</th>
                <th>Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {drawings.map(d => (
                <tr key={d.id}>
                  <td>{d.original_name}</td>
                  <td>{d.uploaded_at}</td>
                  <td>
                    <button onClick={() => copyDrawingLink(d.filename)}>🔗 Αντιγραφή Link</button>
                    <button className="danger" onClick={() => deleteDrawing(d.id)}>Διαγραφή</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'certificates' && (
        <div className="admin-section">
          <h3>Πιστοποιητικά</h3>
          <form onSubmit={handleUploadCert} className="upload-form">
            <input type="file" multiple onChange={e => setCertFiles(e.target.files)} />
            <label>Ημ/νία Λήξης:</label>
            <input type="date" value={certExpiry} onChange={e => setCertExpiry(e.target.value)} />
            <button type="submit">⬆️ Ανέβασμα</button>
          </form>
          <button onClick={clearDuplicateCerts}>🧹 Καθαρισμός διπλότυπων</button>
          <button onClick={checkExpiredCerts}>📧 Έλεγχος & αποστολή email ληξιπρόθεσμων</button>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Όνομα</th>
                <th>Ημ/νία Λήξης</th>
                <th>Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {certificates.map(c => (
                <tr key={c.id}>
                  <td>{c.original_name}</td>
                  <td>
                    {editingExpiryId === c.id ? (
                      <>
                        <input
                          type="date"
                          value={newExpiryDate}
                          onChange={e => setNewExpiryDate(e.target.value)}
                        />
                        <button onClick={() => saveExpiry(c.id)}>💾</button>
                        <button onClick={() => setEditingExpiryId(null)}>✖️</button>
                      </>
                    ) : (
                      <>
                        {c.expiry_date || '—'}{' '}
                        <button onClick={() => startEditExpiry(c.id, c.expiry_date)}>✏️</button>
                      </>
                    )}
                  </td>
                  <td>
                    <button onClick={() => copyCertLink(c.filename)}>🔗 Αντιγραφή Link</button>
                    <button className="danger" onClick={() => deleteCert(c.id)}>Διαγραφή</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'serial' && (
        <div className="admin-section">
          <h3>Serial Certs</h3>
          <form onSubmit={handleUploadSerial} className="upload-form">
            <input type="file" multiple onChange={e => setSerialFiles(e.target.files)} />
            <button type="submit">⬆️ Ανέβασμα</button>
          </form>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Όνομα</th>
                <th>Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {serialCerts.map(s => (
                <tr key={s.id}>
                  <td>{s.original_name}</td>
                  <td>
                    <button onClick={() => copySerialLink(s.filename)}>🔗 Αντιγραφή Link</button>
                    <button className="danger" onClick={() => deleteSerial(s.id)}>Διαγραφή</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'general' && (
        <div className="admin-section">
          <h3>Γενικά Πιστοποιητικά</h3>
          <form onSubmit={handleUploadGeneral} className="upload-form">
            <input type="file" multiple onChange={e => setGeneralFiles(e.target.files)} />
            <button type="submit">⬆️ Ανέβασμα</button>
          </form>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Όνομα</th>
                <th>Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {generalCerts.map(g => (
                <tr key={g.id}>
                  <td>{g.original_name}</td>
                  <td>
                    <button onClick={() => copyGeneralLink(g.filename)}>🔗 Αντιγραφή Link</button>
                    <button className="danger" onClick={() => deleteGeneralCert(g.id)}>Διαγραφή</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'softone' && (
        <div className="admin-section">
          <h3>Πιστοποιητικά SoftOne</h3>
          <form onSubmit={handleUploadSoftone} className="upload-form">
            <input type="file" multiple onChange={e => setSoftoneFiles(e.target.files)} />
            <button type="submit">⬆️ Ανέβασμα</button>
          </form>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Όνομα</th>
                <th>Ημ/νία Ανεβάσματος</th>
                <th>Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {softoneCerts.map(s => (
                <tr key={s.id}>
                  <td>{s.original_name}</td>
                  <td>{s.uploaded_at}</td>
                  <td>
                    <button onClick={() => copySoftoneLink(s.filename)}>🔗 Αντιγραφή Link</button>
                    <button className="danger" onClick={() => deleteSoftoneCert(s.id)}>Διαγραφή</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Admin;